import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  TaskNameItem,
  ApprovalTemplateUser,
  ApprovalTemplateLine,
  ApprovalTemplateVm,
  ApiResponse,
} from '@/api/types'

/**
 * Approval Template (Workflow Approval Master) service
 */
export class ApprovalTemplateService {
  /**
   * Get task names for Approval Task dropdown (GET /api/InboxTasks/TaskNames)
   */
  async getTaskNames(): Promise<TaskNameItem[]> {
    const url = endpoints.inboxTasks.taskNames
    const response = await apiClient.get<ApiResponse<TaskNameItem[]> | TaskNameItem[] | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      if ('success' in payload && Array.isArray(payload.data)) {
        return (payload.data as any[]).map(normalizeTaskName)
      }
      if ('data' in payload && Array.isArray(payload.data)) {
        return (payload.data as any[]).map(normalizeTaskName)
      }
      if (Array.isArray(payload)) {
        return (payload as any[]).map(normalizeTaskName)
      }
    }
    return []
  }

  /**
   * Get users (employees) by branch and department for Employee dropdown
   * GET /api/ApprovalTemplate/GetUsers?branchId=&departmentId=
   */
  async getUsers(branchId: number, departmentId: number): Promise<ApprovalTemplateUser[]> {
    if (!branchId || !departmentId) return []
    const url = endpoints.approvalTemplate.getUsers(branchId, departmentId)
    const response = await apiClient.get<ApiResponse<ApprovalTemplateUser[]> | ApprovalTemplateUser[] | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      if ('success' in payload && Array.isArray(payload.data)) {
        return (payload.data as any[]).map(normalizeUser)
      }
      if ('data' in payload && Array.isArray(payload.data)) {
        return (payload.data as any[]).map(normalizeUser)
      }
      if (Array.isArray(payload)) {
        return (payload as any[]).map(normalizeUser)
      }
    }
    return []
  }

  /**
   * Get approval template by task ID and branch (load existing data)
   * GET /api/ApprovalTemplate/GetByTaskId?taskId=&branchId=
   */
  async getByTaskId(taskId: number, branchId: number): Promise<ApprovalTemplateLine[]> {
    if (!taskId || !branchId) return []
    const url = endpoints.approvalTemplate.getByTaskId(taskId, branchId)
    const response = await apiClient.get<ApiResponse<ApprovalTemplateLine[]> | ApprovalTemplateLine[] | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      let arr: any[] = []
      if ('success' in payload && Array.isArray(payload.data)) arr = payload.data
      else if ('data' in payload && Array.isArray(payload.data)) arr = payload.data
      else if (Array.isArray(payload)) arr = payload
      return arr.map(normalizeTemplateLine)
    }
    return []
  }

  /**
   * Save approval template (POST /api/ApprovalTemplate/Save)
   */
  async save(payload: ApprovalTemplateVm): Promise<void> {
    const url = endpoints.approvalTemplate.save
    await apiClient.post(url, payload)
  }
}

function normalizeTaskName(item: any): TaskNameItem {
  return {
    id: item.id ?? item.Id ?? 0,
    lookupCodeId: item.lookupCodeId ?? item.LookupCodeId,
    lookupNameEn: item.lookupNameEn ?? item.LookupNameEn ?? '',
    lookupNameAr: item.lookupNameAr ?? item.LookupNameAr ?? '',
    isActive: item.isActive ?? item.IsActive,
    parentCode: item.parentCode ?? item.ParentCode,
    createdById: item.createdById ?? item.CreatedById,
    createdDate: item.createdDate ?? item.CreatedDate,
    updatedById: item.updatedById ?? item.UpdatedById,
    updatedDate: item.updatedDate ?? item.UpdatedDate,
    companyId: item.companyId ?? item.CompanyId,
    branchId: item.branchId ?? item.BranchId,
  }
}

function normalizeUser(item: any): ApprovalTemplateUser {
  const userId = item.userId ?? item.UserId ?? item.id ?? item.Id ?? 0
  const userName = item.userName ?? item.UserName ?? ''
  const nameEn = item.userNameEn ?? item.UserNameEn ?? item.lookupNameEn ?? item.LookupNameEn ?? ''
  const nameAr = item.userNameAr ?? item.UserNameAr ?? item.lookupNameAr ?? item.LookupNameAr ?? ''
  const displayName = userName || item.fullName || item.FullName || nameEn || nameAr || ''
  return {
    id: item.id ?? item.Id,
    userId,
    userName,
    userNameEn: nameEn,
    userNameAr: nameAr,
    fullName: displayName,
    employeeId: item.employeeId ?? item.EmployeeId,
    empId: item.empId ?? item.EmpId,
    departmentId: item.departmentId ?? item.DepartmentId,
    branchId: item.branchId ?? item.BranchId,
  }
}

function normalizeTemplateLine(item: any): ApprovalTemplateLine {
  return {
    lineId: item.lineId ?? item.LineId ?? item.id ?? item.Id ?? 0,
    approvalTaskId: item.approvalTaskId ?? item.ApprovalTaskId ?? 0,
    seqNo: item.seqNo ?? item.SeqNo ?? 0,
    userId: item.userId ?? item.UserId ?? 0,
    isActive: item.isActive === true || item.IsActive === true || item.isActive === 1 || item.IsActive === 1,
    isDeleted: item.isDeleted === true || item.IsDeleted === true,
    departmentId: item.departmentId ?? item.DepartmentId,
    departmentName: item.departmentName ?? item.DepartmentName,
    userName: item.userName ?? item.UserName,
  }
}

export const approvalTemplateService = new ApprovalTemplateService()
